import { BehaviorSubject, fromEvent, interval, Observable, tap, map, filter, Subject, takeUntil } from 'rxjs';
import { preventAnimation, accessibilityEnabledListener } from '../services/accessibility.service';
const LEFT = -1;
const RIGHT = 1;
const CLICK_BUFFER = 3; // buffer in pixels to prevent a click from being interpreted as a swipe
const DEFAULT_SLIDE_SELECTOR = '.js-utk-carousel-slide';
const DEFAULT_BUTTON_SELECTOR = '.js-utk-carousel-selection';
// TODO: merge with GlobalBanner.js
export class Carousel {
    constructor(container, config, device) {
        this.container = container;
        this.config = config;
        this.device = device;
    }
    config;
    container;
    device;
    slides;
    buttons;
    rotationSub$;
    selectedSlide$ = new BehaviorSubject(undefined);
    stopRotation$ = new Subject();
    destroy$ = new Subject();
    swipeListenersAdded = false;
    setup() {
        this.slides = Array.from(this.container.querySelectorAll(this.config.slideSelector || DEFAULT_SLIDE_SELECTOR));
        this.buttons = Array.from(this.container.querySelectorAll(this.config.buttonSelector || DEFAULT_BUTTON_SELECTOR));
        if (this.slides.length > 1) {
            console.log(`${this.constructor.name}: setting up carousel for ${this.device}`);
            this.selectedSlide$.next(this.slides[0].dataset.id);
            this.selectedSlide$.pipe(takeUntil(this.destroy$), tap((id) => {
                this.slides.forEach((b) => {
                    if (b.dataset.id === id) {
                        b.classList.add("selected");
                    }
                    else {
                        b.classList.remove("selected");
                    }
                });
                this.buttons.forEach((b) => {
                    if (b.dataset.id === id) {
                        b.classList.add("selected");
                    }
                    else {
                        b.classList.remove("selected");
                    }
                });
            })).subscribe();
            fromEvent(this.buttons, "click").pipe(takeUntil(this.destroy$), map(e => e.target), filter(el => !el.classList.contains("selected")), tap(el => this.selectedSlide$.next(el.dataset.id)), tap(() => this.resetRotation())).subscribe();
            this.startRotation();
            // accessibility concerns
            fromEvent(this.container, "mouseenter").pipe(takeUntil(this.destroy$), tap(() => this.stopRotation())).subscribe();
            fromEvent(this.container, "mouseleave").pipe(takeUntil(this.destroy$), tap(() => this.startRotation())).subscribe();
            accessibilityEnabledListener().pipe(takeUntil(this.destroy$), tap(() => this.stopRotation())).subscribe();
            if (this.device === "mobile" && !this.swipeListenersAdded) {
                this.addSwipeListeners(this.container);
            }
        }
        else if (this.slides.length === 1) {
            console.log(`GlobalBanner: setting up for one global banner for ${this.device}`);
            this.slides[0].classList.add("global-banner__banner--selected");
            this.rotationSub$ = new Observable().pipe(takeUntil(this.destroy$)).subscribe(); // dummy observable to prevent starting rotation (todo: better solution)
        }
    }
    reset() {
        this.debug("refreshing setup");
        this.destroy$.next(undefined);
        this.setup();
    }
    getNextSlideId(direction) {
        const selectedIdx = this.slides.findIndex((b) => b.classList.contains("selected"));
        const nextIdx = selectedIdx + (direction * 1);
        const newIdx = nextIdx === (this.slides.length) ? 0 : nextIdx === -1 ? this.slides.length - 1 : nextIdx;
        const nextSlide = this.slides[newIdx];
        return nextSlide.dataset.id;
    }
    stopRotation() {
        this.debug("stopping rotation");
        this.stopRotation$.next(undefined);
    }
    startRotation() {
        const rotationSpeed = this.config.rotation;
        if (!preventAnimation() && (!this.rotationSub$ || this.rotationSub$.closed)) {
            this.debug("starting rotation");
            let obs;
            if (rotationSpeed) {
                const rotationMillis = +rotationSpeed * 1000;
                obs = interval(rotationMillis).pipe(takeUntil(this.destroy$), takeUntil(this.stopRotation$), map(() => this.getNextSlideId(RIGHT)), tap(id => this.selectedSlide$.next(id)));
            }
            else {
                obs = new Observable().pipe(takeUntil(this.destroy$), takeUntil(this.stopRotation$)); // dummy observable;
            }
            this.rotationSub$ = obs.subscribe();
        }
    }
    resetRotation() {
        this.debug("resetting rotation");
        this.stopRotation();
        this.startRotation();
    }
    addSwipeListeners(el) {
        this.debug("adding swipe listeners");
        let touchstartX = 0;
        let touchendX = 0;
        el.addEventListener('touchstart', e => {
            this.debug("touchstart");
            touchstartX = e.changedTouches[0].screenX;
        }, { passive: true });
        el.addEventListener('touchend', e => {
            this.debug("touchend");
            touchendX = e.changedTouches[0].screenX;
            const diff = Math.abs(touchstartX - touchendX);
            if (diff < CLICK_BUFFER) {
                // click
                return;
            }
            let direction = RIGHT;
            if (touchendX > touchstartX) {
                direction = LEFT;
            }
            const newSlideId = this.getNextSlideId(direction);
            this.selectedSlide$.next(newSlideId);
            this.resetRotation();
        });
        this.swipeListenersAdded = true;
    }
    debug(msg) {
        console.debug(`${this.constructor.name} ${this.device || ""}: ${msg}`);
    }
}
